import React, { useRef, useState, useEffect } from 'react';
import { Download, Loader2 } from 'lucide-react';

// Diploma template is 2746 x 1936 pixels
// Key positions (measured from template):
// Student name line: between "ESTE DIPLOMA CERTIFICA QUE:" and "HA CULMINADO..." → Y ≈ 820, centered X ≈ 1500
// Date line "FECHA DE EMISIÓN": bottom left → Y ≈ 1875, X ≈ 530
// Certificate number "Nº DE CERTIFICADO": bottom right → Y ≈ 1875, X ≈ 2350

export function DiplomaGenerator({ studentName, date, certificateNumber, onClose }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [generating, setGenerating] = useState(false);

  const now = date ? new Date(date) : new Date();
  const day = now.getDate().toString().padStart(2, '0');
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const year = now.getFullYear().toString();
  const certNum = certificateNumber || `TA-${Date.now().toString(36).toUpperCase().slice(-6)}`;

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      // Draw diploma template
      ctx.drawImage(img, 0, 0);

      const W = img.width;   // 2746
      const H = img.height;  // 1936

      // Student name - dark color, centered, on the name line
      const name = studentName || 'Estudiante';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#1a1a1a';

      // Auto-size font based on name length
      let fontSize = name.length > 25 ? 70 : name.length > 18 ? 85 : 100;
      ctx.font = `bold italic ${fontSize}px "Georgia", "Times New Roman", serif`;
      ctx.fillText(name, W * 0.547, H * 0.425);

      // Date on the "FECHA DE EMISIÓN: __/__/__" line
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#333333';
      ctx.font = '28px "Georgia", serif';
      ctx.fillText(`${day} / ${month} / ${year}`, W * 0.22, H * 0.965);

      // Certificate number on the "Nº DE CERTIFICADO:" line at bottom right
      ctx.textAlign = 'center';
      ctx.fillText(certNum, W * 0.84, H * 0.965);

      setReady(true);
    };
    img.src = '/diploma_template.png';
  }, [studentName, day, month, year, certNum]);

  const handleDownload = () => {
    setGenerating(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Diploma_Trading_Alienigena_${(studentName || 'Estudiante').replace(/\s+/g, '_')}.png`;
      a.click();
      URL.revokeObjectURL(url);
      setGenerating(false);
    }, 'image/png');
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/95 overflow-y-auto flex items-center justify-center p-4" data-testid="diploma-modal">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-green-400" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            TU DIPLOMA DE CERTIFICACIÓN
          </h2>
          <p className="text-emerald-100/50 text-sm">Descarga tu diploma personalizado</p>
        </div>

        <div className="glass-panel rounded-lg p-2 mb-4 overflow-hidden">
          <canvas
            ref={canvasRef}
            className="w-full h-auto rounded"
            style={{ display: ready ? 'block' : 'none' }}
          />
          {!ready && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={handleDownload}
            disabled={!ready || generating}
            className="bg-green-500 text-black px-6 py-3 rounded-lg font-bold hover:bg-green-400 transition-all flex items-center space-x-2 disabled:opacity-50"
            data-testid="diploma-download"
          >
            {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
            <span>{generating ? 'Generando...' : 'Descargar Diploma'}</span>
          </button>
          <button onClick={onClose} className="text-emerald-100/50 hover:text-emerald-400 text-sm">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
